import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';

export class FormModel<T extends Record<string, any>> {
  readonly formGroup: TypedFormGroup<T>;

  private readonly fb = new FormBuilder();
  private readonly asyncInit: any = {};

  get controls() {
    return this.formGroup.controls;
  }

  get dirty(): boolean {
    return this.formGroup.dirty;
  }

  get value(): T {
    return this.formGroup.getRawValue();
  }

  set value(value: T) {
    this.formGroup.setValue(value);
  }

  constructor(
    initialValue: T,
    initialState: { [K in keyof T]?: FieldState } = {}
  ) {
    const controlsConfig: Record<string, FormControl> = {};

    for (const key of Object.keys(initialValue)) {
      controlsConfig[key] = this.fb.control({
        value: initialValue[key],
        disabled: false,
        ...initialState[key],
      });
    }

    this.formGroup = this.fb.group(controlsConfig) as TypedFormGroup<T>;
  }

  init(values: Partial<T>): void {
    for (const key of Object.keys(values)) {
      if (!this.asyncInit[key]) {
        this.asyncInit[key] = true;
        this.formGroup.patchValue({
          [key]: values[key],
        });
      }
    }
  }

  set(values: Partial<T>): void {
    this.formGroup.patchValue(values);
  }

  enable(key: keyof T & string, emitEvent = true): void {
    this.formGroup.get(key)!.enable({ emitEvent });
  }

  disable(key: keyof T & string, emitEvent = true): void {
    this.formGroup.get(key)!.disable({ emitEvent });
  }
}

interface FieldState {
  disabled: boolean;
}

interface TypedFormGroup<T extends Record<string, any>> extends FormGroup {
  controls: {
    [K in keyof T]: AbstractControl;
  };

  get(path: keyof T): AbstractControl;
  get(path: Array<string | number> | string): AbstractControl | null;
}
